// Centralized constants for optidev-generated element IDs.
// These IDs are assigned to temporary elements (layouts, sections, section children)
// that exist only in the iframe DOM — they are NOT file paths.
//
// Format examples:
//   Layout parent:  optidev-columns-2-1741013455000-abc9d2
//   Layout child:   optidev-col-1-1741013455000-abc9d2
//   Section parent: optidev-section-hero-1-1741013455000-abc9d2
//   Section child:  optidev-section-hero-1-1741013455000-abc9d2-c3

const GENERATED_ID_PREFIX = "optidev-";
const LAYOUT_COL_PREFIX = "optidev-col-";
const LAYOUT_ROW_PREFIX = "optidev-row-";
const SECTION_CHILD_PATTERN = /^optidev-.*-c\d+$/;
const SECTION_CHILD_EXTRACT = /^(optidev-.*)-c\d+$/;

/** True when the path is a generated optidev ID (not a file:line:col path). */
function isGeneratedId(path: string | undefined | null): boolean {
  if (!path) return false;
  return path.startsWith(GENERATED_ID_PREFIX);
}

/** True when the path identifies a layout column child. */
function isLayoutColumn(path: string): boolean {
  return path.startsWith(LAYOUT_COL_PREFIX);
}

/** True when the path identifies a layout row child. */
function isLayoutRow(path: string): boolean {
  return path.startsWith(LAYOUT_ROW_PREFIX);
}

/** True when the path identifies any layout child (column or row). */
function isLayoutChild(path: string): boolean {
  return isLayoutColumn(path) || isLayoutRow(path);
}

/** True when the path identifies a section child (e.g. "optidev-nav-xxx-c1"). */
function isSectionChild(path: string | undefined | null): boolean {
  if (!path) return false;
  return SECTION_CHILD_PATTERN.test(path);
}

/** Extract the parent section ID from a section child path, or null. */
function getSectionParentId(childPath: string): string | null {
  const match = childPath.match(SECTION_CHILD_EXTRACT);
  return match ? match[1] : null;
}

/** Build a unique generated ID for a component instance. */
function makeGeneratedId(componentType: string): string {
  return `${GENERATED_ID_PREFIX}${componentType}-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/** Build a unique generated ID for a layout child (column/row). */
function makeLayoutChildId(prefix: "col" | "row", index: number): string {
  return `${GENERATED_ID_PREFIX}${prefix}-${index}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export {
  GENERATED_ID_PREFIX,
  LAYOUT_COL_PREFIX,
  LAYOUT_ROW_PREFIX,
  isGeneratedId,
  isLayoutColumn,
  isLayoutRow,
  isLayoutChild,
  isSectionChild,
  getSectionParentId,
  makeGeneratedId,
  makeLayoutChildId,
};
